/**
 * The "are these two companies connected?" predicate, as a SQL fragment.
 *
 * It lives in `lib/` rather than in `modules/companies`, which owns
 * `companies_service_company_links`, for one reason: `modules/companies`
 * already imports `modules/audits` (`patch-template-answers.service.ts` reads
 * an audit to stamp provenance), and the audits queries are the callers that
 * need this. Importing the companies barrel back from audits would close a
 * module cycle — so the shared piece moves to a third place, as
 * `docs/MODULE_ARCHITECTURE_GUIDELINES.md` §7.1 prescribes. `modules/companies`
 * re-exports it, so the owning domain is still where you find it.
 *
 * The JS form of the same question is `hasAnyConnectionDb` in
 * `modules/companies/core/db/company-links/has-any-connection.ts`; the two must
 * stay in step.
 */

/**
 * A SQL fragment that is true when the two companies are connected — for
 * callers that must decide it *per row* inside one query, where a round trip
 * per row is not an option (the audits list, which evaluates it for every
 * audit it returns).
 *
 * `companyExpr` and `otherExpr` are SQL **expressions**, not values: a bound
 * placeholder (`$1`) or a column reference
 * (`audits_service_audits.company_id`). Binding stays with the caller, so this
 * builder never touches the parameter list.
 *
 * Two things it keeps in step with `hasAnyConnectionDb`:
 *
 * - **Both directions.** Links are normally written twice, but not
 *   invariably — 30 rows are deliberately one-directional (see
 *   `202609101200-delta-2447-unlink-carhartt-resurrected-suppliers.ts`), so
 *   checking one direction would silently drop them.
 * - **A company is connected to itself**, stated as an equality rather than
 *   left to the data: only about a sixth of companies have the self-link row
 *   (`company_id = other_id`) that would otherwise carry it.
 */
export const buildCompaniesConnectedSql = (
  companyExpr: string,
  otherExpr: string,
): string => `(
        ${companyExpr} = ${otherExpr}
        OR EXISTS (
          SELECT 1
          FROM companies_service_company_links company_link
          WHERE (company_link.company_id = ${companyExpr} AND company_link.other_id = ${otherExpr})
             OR (company_link.company_id = ${otherExpr} AND company_link.other_id = ${companyExpr})
        )
      )`;
